import { Routes, Route } from 'react-router-dom';
import { useMemo } from 'react';
import { getAccessToken } from '../lib/api';
import { decodeRoleFromToken, type AppRole } from '../lib/role';
import Layout from './Layout';
import MentorLayout from './MentorLayout';
import DashboardPage from '../pages/DashboardPage';
import KnowledgeBasePage from '../pages/KnowledgeBasePage';
import InterviewPage from '../pages/InterviewPage';
import PipelinePage from '../pages/PipelinePage';
import TrackerPage from '../pages/TrackerPage';
import LinkedInPage from '../pages/LinkedInPage';
import MentorsPage from '../pages/MentorsPage';
import MentorDashboardPage from '../pages/MentorDashboardPage';
import MentorKBPage from '../pages/MentorKBPage';
import MentorDocumentsPage from '../pages/MentorDocumentsPage';
import MentorSuggestionsPage from '../pages/MentorSuggestionsPage';

/**
 * RoleRouter — determines the user's role from the in-memory id_token
 * and renders the appropriate layout and routes.
 *
 * Security notes:
 *   - Role is decoded from the id_token (custom:role claim) without an API call (R1.4)
 *   - Unknown/missing role defaults to 'user' (least-privilege fallback — R1.5)
 *   - No navigation flash — role resolved synchronously from in-memory token (R1.6)
 *
 * Requirements: 1.1, 1.4, 1.5, 1.6
 */
export default function RoleRouter() {
  const role: AppRole = useMemo(() => {
    const token = getAccessToken();
    if (!token) return 'user';
    return decodeRoleFromToken(token);
  }, []);

  if (role === 'mentor') {
    return (
      <MentorLayout>
        <Routes>
          <Route path="/" element={<MentorDashboardPage />} />
          <Route path="/mentor/kb" element={<MentorKBPage />} />
          <Route path="/mentor/documents" element={<MentorDocumentsPage />} />
          <Route path="/mentor/suggestions" element={<MentorSuggestionsPage />} />
        </Routes>
      </MentorLayout>
    );
  }

  // user / admin — existing applicant layout
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/kb" element={<KnowledgeBasePage />} />
        <Route path="/interview" element={<InterviewPage />} />
        <Route path="/pipeline" element={<PipelinePage />} />
        <Route path="/tracker" element={<TrackerPage />} />
        <Route path="/linkedin" element={<LinkedInPage />} />
        <Route path="/mentors" element={<MentorsPage />} />
      </Routes>
    </Layout>
  );
}
